import React, { Component } from 'react';
import { motion } from 'framer-motion';
import { Ticket, Hotel, Plane, Car, ExternalLink, Tag } from 'lucide-react';
import { useLanguage } from './LanguageContext';
export function TicketsEvents() {
  const {
    t
  } = useLanguage();
  const whatsappNumber = '14699895608';
  const deals = [{
    title: t('deals.tickets'),
    desc: t('deals.ticketsDesc'),
    icon: <Ticket className="text-brazil-yellow" size={24} data-id="element-350" />,
    cta: t('deals.findTickets'),
    link: `https://wa.me/${whatsappNumber}?text=${encodeURIComponent("Hello, I'm interested in purchasing FIFA World Cup 2026 tickets for Brazil's matches. I'd like to apply the QTAP26 discount. Could you share available packages and pricing? Thank you.")}`
  }, {
    title: t('deals.hotels'),
    desc: t('deals.hotelsDesc'),
    icon: <Hotel className="text-brazil-yellow" size={24} data-id="element-351" />,
    cta: t('deals.bookHotels'),
    link: `https://wa.me/${whatsappNumber}?text=${encodeURIComponent("Hello, I'm looking for hotel accommodations for the FIFA World Cup 2026 in the USA. I'd like to apply the QTAP26 discount. Could you share available options and rates? Thank you.")}`
  }, {
    title: t('deals.flights'),
    desc: t('deals.flightsDesc'),
    icon: <Plane className="text-brazil-yellow" size={24} data-id="element-352" />,
    cta: t('deals.searchFlights'),
    link: `https://wa.me/${whatsappNumber}?text=${encodeURIComponent("Hello, I'm searching for flights to the FIFA World Cup 2026 host cities. I'd like to apply the QTAP26 discount. Could you help me find the best options? Thank you.")}`
  }, {
    title: t('deals.transport'),
    desc: t('deals.transportDesc'),
    icon: <Car className="text-brazil-yellow" size={24} data-id="element-353" />,
    cta: t('deals.planTransport'),
    link: `https://wa.me/${whatsappNumber}?text=${encodeURIComponent("Hello, I need ground transportation for the FIFA World Cup 2026. I'd like to apply the QTAP26 discount. Could you share available transport packages? Thank you.")}`
  }];
  return <section id="deals" className="py-12 md:py-24 bg-[#111] border-t border-white/5" data-id="element-354">
      <div className="container mx-auto px-6 md:px-12" data-id="element-355">
        <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-356">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-brazil-green font-oswald uppercase tracking-widest text-sm font-bold mb-3" data-id="element-357">
            {t('deals.label')}
          </motion.h2>
          <motion.h3 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-3xl md:text-5xl font-oswald font-bold uppercase mb-6" data-id="element-358">
            {t('deals.title1')}{' '}
            <span className="text-brazil-yellow" data-id="element-359">{t('deals.title2')}</span>
          </motion.h3>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="text-gray-400" data-id="element-360">
            {t('deals.desc')}
          </motion.p>
        </div>

        {/* Promo Banner */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-gradient-to-r from-brazil-dark-green to-brazil-green rounded-2xl p-6 md:p-8 mb-12 flex flex-col md:flex-row items-center justify-between gap-6 border border-brazil-yellow/30 shadow-[0_0_30px_rgba(0,151,57,0.2)]" data-id="element-361">
          <div className="flex items-center gap-4" data-id="element-362">
            <div className="bg-brazil-yellow text-brazil-black p-3 rounded-full" data-id="element-363">
              <Tag size={24} data-id="element-364" />
            </div>
            <div data-id="element-365">
              <h4 className="font-oswald text-xl md:text-2xl font-bold uppercase text-white" data-id="element-366">
                {t('deals.unlockVip')}
              </h4>
              <p className="text-white/80 text-sm" data-id="element-367">{t('deals.applyCode')}</p>
            </div>
          </div>
          <div className="bg-brazil-black/50 border border-brazil-yellow/50 px-6 py-3 rounded-lg font-oswald text-2xl tracking-widest text-brazil-yellow font-bold select-all" data-id="element-368">
            QTAP26
          </div>
        </motion.div>

        {/* Deals Grid — 2 columns on mobile */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6" data-id="element-369">
          {deals.map((deal, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: index * 0.08
        }} className="bg-[#0a0a0a] p-4 md:p-8 rounded-xl md:rounded-2xl border border-white/5 hover:border-brazil-yellow transition-colors duration-300 flex flex-col h-full group" data-id="element-370">
              <div className="bg-white/5 w-10 h-10 md:w-14 md:h-14 rounded-lg md:rounded-xl flex items-center justify-center mb-3 md:mb-6 group-hover:bg-brazil-yellow/10 transition-colors" data-id="element-371">
                {deal.icon}
              </div>
              <h4 className="font-oswald text-sm md:text-xl font-bold uppercase mb-1 md:mb-2 text-white leading-tight" data-id="element-372">
                {deal.title}
              </h4>
              <p className="text-[10px] md:text-sm text-gray-400 mb-4 md:mb-8 flex-grow leading-relaxed" data-id="element-373">
                {deal.desc}
              </p>
              <a href={deal.link} target="_blank" rel="noopener noreferrer" className="w-full bg-[#25D366] hover:bg-[#20bd5a] text-white px-3 md:px-6 py-2.5 md:py-3 rounded-full font-oswald uppercase tracking-wider text-[10px] md:text-sm font-bold transition-all duration-300 flex items-center justify-center gap-1.5 md:gap-2 min-h-[44px]" data-id="element-374">
                {deal.cta}{' '}
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3 md:w-4 md:h-4" data-id="element-375">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" data-id="element-376" />
                </svg>
              </a>
            </motion.div>)}
        </div>
      </div>
    </section>;
}